import React from 'react';

interface SkillComparisonChartProps {
  title?: string;
  subtitle?: string;
  playerName: string;
  overallScore: number;
  maxScore?: number;
  skills: {
    name: string;
    score: number;
    maxScore?: number;
    color?: string;
    isTopSkill?: boolean;
    isGrowthZone?: boolean;
  }[];
  className?: string;
}

const defaultColors = [
  'bg-blue-500',
  'bg-green-500',
  'bg-yellow-500',
  'bg-purple-500',
  'bg-red-500',
  'bg-orange-500'
];

export const SkillComparisonChart: React.FC<SkillComparisonChartProps> = ({
  title = "Skill Comparison",
  subtitle,
  playerName,
  overallScore,
  maxScore = 100,
  skills,
  className
}) => {
  const overallPercentage = (overallScore / maxScore) * 100;

  return (
    <div className={`p-6 bg-cream-50 rounded-lg ${className}`}>
      {title && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          {subtitle && (
            <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
          )}
        </div>
      )}

      <div className="relative">
        {/* Overall Score Line */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t-2 border-gray-800"></div>
          </div>
          <div className="relative flex justify-center">
            <div className="bg-cream-50 px-2">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-gray-800 transform rotate-45 mr-2"></div>
                <span className="text-sm font-semibold text-gray-800">
                  {playerName}'s Overall Score
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Skills Bars */}
        <div className="space-y-4">
          {skills.map((skill, index) => {
            const skillMaxScore = skill.maxScore || maxScore;
            const skillPercentage = (skill.score / skillMaxScore) * 100;
            const color = skill.color || defaultColors[index % defaultColors.length];

            return (
              <div key={index} className="relative">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-700 mr-2 break-words">
                      {skill.name}
                    </span>
                    {skill.isTopSkill && (
                      <div className="bg-orange-400 text-white text-xs px-2 py-1 rounded-full flex items-center">
                        <span className="mr-1">🏆</span>
                        TOP SKILL
                      </div>
                    )}
                    {skill.isGrowthZone && (
                      <div className="bg-green-400 text-white text-xs px-2 py-1 rounded-full flex items-center">
                        <span className="mr-1">📈</span>
                        GROWTH ZONE
                      </div>
                    )}
                  </div>
                </div>

                <div className="relative">
                  <div className="w-full bg-gray-200 rounded-full h-6">
                    <div
                      className={`h-6 rounded-full transition-all duration-500 ${color}`}
                      style={{ width: `${skillPercentage}%` }}
                    />
                  </div>

                  {/* Overall score indicator line */}
                  <div
                    className="absolute top-0 bottom-0 w-0.5 bg-gray-800 z-10"
                    style={{ left: `${overallPercentage}%` }}
                  >
                    <div className="absolute -top-1 -left-1 w-3 h-3 bg-gray-800 transform rotate-45"></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend or additional info */}
        <div className="mt-4 text-xs text-gray-600">
          <p>Chart compares each skill category to {playerName}'s overall performance level.</p>
        </div>
      </div>
    </div>
  );
};